/**
 * Admin company branding API hooks — letterhead logo + signature.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

export interface CompanyBrandingDto {
  id: string;
  companyName: string;
  addressLines: string[];
  logoUrl: string | null;
  signatureUrl: string | null;
  hasLogo: boolean;
  hasSignature: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export const brandingKeys = {
  all: ['branding'] as const,
  detail: () => [...brandingKeys.all, 'detail'] as const,
};

export function useCompanyBranding(enabled = true) {
  return useQuery({
    queryKey: brandingKeys.detail(),
    queryFn: () => api.get<CompanyBrandingDto>('/api/branding'),
    enabled,
  });
}

export function useUpdateCompanyBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { companyName?: string; addressLines?: string[] }) =>
      api.put<CompanyBrandingDto>('/api/branding', body),
    onSuccess: (data) => {
      qc.setQueryData(brandingKeys.detail(), data);
    },
  });
}

export function useUploadBrandingLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      api.upload<CompanyBrandingDto>('/api/branding/logo', formData),
    onSuccess: (data) => {
      qc.setQueryData(brandingKeys.detail(), data);
    },
  });
}

export function useUploadBrandingSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      api.upload<CompanyBrandingDto>('/api/branding/signature', formData),
    onSuccess: (data) => {
      qc.setQueryData(brandingKeys.detail(), data);
    },
  });
}
